// Topic notes, stored as markdown files in Cloud Storage.
//
// This mirrors prepgen-public's topic-notes model (see prepgen-public
// services/api.ts -> topicApi.getNoteContent/saveNoteContent), which reads and
// writes notes.md directly from the client via the Firebase Storage SDK — no
// backend endpoint and no Firestore document.
//
// The one multi-tenancy adaptation for B2B: every write is ORG-SCOPED. An
// institute edits its OWN copy under orgs/{orgId}/..., and we NEVER write to
// Prepgen's canonical shared path (exams/...) — doing so would corrupt the
// managed notes seen by every other org. We only READ the canonical path, to
// seed a fresh customizable copy ("Start from Prepgen's notes").

import { ref, getDownloadURL, uploadString, list } from 'firebase/storage';
import { storage } from '../config/firebase';
import { withStorageAccess } from './storageAccess';

const NOTES_FILE = 'notes.md';

// Storage rules refuse notes of 1 MB or more (docs/storage.rules in prepgen).
// Checked here too so an oversized note gets its own message, not a permission
// error. Prepgen's own topic notes in staging are all under 20 KB.
const MAX_NOTE_BYTES = 1024 * 1024;

// Institute's own editable copy — safe to read AND write.
function orgNotePath(orgId, examId, sectionId, topicId, fileName = NOTES_FILE) {
  return `orgs/${orgId}/exams/${examId}/sections/${sectionId}/topics/${topicId}/${fileName}`;
}

// Prepgen's canonical shared notes — READ ONLY from B2B. Never pass this to a write.
function canonicalNotePath(examId, sectionId, topicId, fileName = NOTES_FILE) {
  return `exams/${examId}/sections/${sectionId}/topics/${topicId}/${fileName}`;
}

// Read a markdown file from Storage. Returns its text, or null when the file
// simply doesn't exist yet (a normal state — the topic hasn't been customized).
// Any other error (e.g. permission denied, CORS) propagates so the UI can show it.
async function readMarkdown(path) {
  let url;
  try {
    url = await getDownloadURL(ref(storage, path));
  } catch (err) {
    if (err?.code === 'storage/object-not-found') return null;
    throw err;
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to download notes (${res.status})`);
  return res.text();
}

// The institute's customized notes for a topic, or null if none exist yet.
export function getOrgNoteContent(orgId, examId, sectionId, topicId, fileName = NOTES_FILE) {
  return readMarkdown(orgNotePath(orgId, examId, sectionId, topicId, fileName));
}

// Prepgen's canonical notes for a topic (read-only), used to seed a copy.
export function getCanonicalNoteContent(examId, sectionId, topicId, fileName = NOTES_FILE) {
  return readMarkdown(canonicalNotePath(examId, sectionId, topicId, fileName));
}

// Write (create or overwrite) the institute's copy of a topic's notes.
export async function saveOrgNoteContent(orgId, examId, sectionId, topicId, content, fileName = NOTES_FILE) {
  if (new TextEncoder().encode(content).length >= MAX_NOTE_BYTES) {
    throw new Error('These notes are too long to save. Keep them under 1 MB, or split them across topics.');
  }
  const path = orgNotePath(orgId, examId, sectionId, topicId, fileName);
  return withStorageAccess(orgId, () => uploadString(ref(storage, path), content, 'raw', {
    contentType: 'text/markdown; charset=utf-8',
  }));
}

// Whether the institute has saved its own notes for any topic. Only notes are
// written under orgs/{orgId}/exams/, and Storage lists a prefix only while an
// object exists beneath it, so a single entry at the top is enough to know.
export async function hasOrgNotes(orgId) {
  const res = await list(ref(storage, `orgs/${orgId}/exams`), { maxResults: 1 });
  return res.prefixes.length > 0 || res.items.length > 0;
}

// Markdown can embed images by gs:// URL or by a firebasestorage.googleapis.com
// URL. Convert either into the object's storage path so it can be turned into a
// fetchable download URL. Returns null for ordinary http(s) image URLs, which
// need no resolution. (Ported from prepgen-public NotesViewer.tsx.)
export function getStoragePathFromImageSrc(src) {
  if (!src) return null;

  if (src.startsWith('gs://')) {
    const withoutScheme = src.slice(5);
    const slashIndex = withoutScheme.indexOf('/');
    return slashIndex === -1 ? null : withoutScheme.slice(slashIndex + 1);
  }

  if (src.includes('firebasestorage.googleapis.com')) {
    try {
      const url = new URL(src);
      const marker = '/o/';
      const markerIndex = url.pathname.indexOf(marker);
      if (markerIndex === -1) return null;
      const encodedPath = url.pathname.slice(markerIndex + marker.length);
      return decodeURIComponent(encodedPath);
    } catch {
      return null;
    }
  }

  return null;
}

// Resolve an image src used in markdown to something the browser can load.
// gs://-style and firebasestorage URLs become signed download URLs; anything
// else is returned unchanged.
export async function resolveImageSrc(src) {
  const storagePath = getStoragePathFromImageSrc(src);
  if (!storagePath) return src;
  return getDownloadURL(ref(storage, storagePath));
}
